import axios from "axios";
import { getAuthHeaders } from "../../../utils/authUtils";
import { API_BASE_URL } from "../../../environment";

const checkSalesUserRole = (): {
  isSalesUser: boolean;
  isClientAdmin: boolean;
  userId: number | null;
} => {
  let isSalesUser = false;
  let isClientAdmin = false;
  let userId = null;

  const authResponse = localStorage.getItem("authResponse");

  if (authResponse) {
    try {
      const auth = JSON.parse(authResponse);
      const tenantId = localStorage.getItem("selectedTenantId");
      const tenantObj = Array.isArray(auth.tenants)
        ? auth.tenants.find((t: any) => String(t.tenant_id) === tenantId)
        : null;

      isSalesUser = tenantObj?.role_key === "SALESUSER";
      isClientAdmin = tenantObj?.role_key === "CLIENTADMIN";
      userId = auth.user_id || null;
    } catch (e) {
      console.warn("Could not parse auth response to check for user role", e);
    }
  }

  return { isSalesUser, isClientAdmin, userId };
};

export interface KanbanStage {
  lifecycle_type_stage_id: number;
  tenant_id: number;
  lifecycle_type_id: number;
  stage_name: string;
  stage_order: number;
  count: number;
}

export interface KanbanLead {
  lead_id: number;
  title: string;
  lifecycle_source: {
    lifecycle_source_id: number;
    lifecycle_source_key: string;
    lifecycle_source_display_name: string;
  };
  currency_code: string;
  priority: string;
  products_n_services: string;
  score: number | null;
  estimated_value: number;
  company: {
    company_id: number;
    company_name: string;
    company_logo_url: string | null;
  } | null;
  primary_contact: {
    contact_id: number;
    full_name: string;
    avatar_url: string | null;
  } | null;
  lifecycle_type_stage: {
    lifecycle_type_stage_id: number;
    tenant_id: number;
    lifecycle_type_id: number;
    stage_name: string;
    stage_order: number;
  };
}

export interface KanbanLeadStage extends KanbanStage {
  leads: KanbanLead[];
}

export interface KanbanData {
  count: number;
  next: string | null;
  previous: string | null;
  results: {
    stages: KanbanLeadStage[];
  };
}

export const LeadKanbanService = {
  async fetchKanbanData(
    tenantId: number,
    limit: number = 20,
    page: number = 1,
    filters?: Record<string, any>
  ): Promise<KanbanData> {
    const { isSalesUser, userId } = checkSalesUserRole();

    const archivedFilter = filters?.archivedFilter || "no";
    let isArchiveParam = "";
    
    if (archivedFilter === "yes") {
      isArchiveParam = "&is_archive=true";
    } else if (archivedFilter === "no") {
      isArchiveParam = "&is_archive=false";
    }

    let url = `${API_BASE_URL}/lookup/kanban/lead/?tenant_id=${tenantId}&limit=${limit}&page=${page}${isArchiveParam}`;

    if (isSalesUser && userId) {
      url += `&sales_user_id=${userId}`;
    }

    if (filters?.statusFilter) {
      url += `&lifecycle_type_stage_id=${filters.statusFilter}`;
    }

    if (filters) {
      Object.keys(filters).forEach((key) => {
        if (!["searchTerm", "dateRange", "statusFilter", "archivedFilter", "sortBy", "sortOrder", "industryFilter", "countryFilter"].includes(key)) {
          const val = filters[key];
          if (val !== undefined && val !== null && val !== "") {
            url += `&${key}=${encodeURIComponent(String(val))}`;
          }
        }
      });
    }

    try {
      const response = await axios.get(url, {
        headers: getAuthHeaders(),
      });

      return response.data;
    } catch (error) {
      console.error("Error fetching kanban data:", error);
      throw error;
    }
  },

  async updateLeadStage(
    leadId: number,
    stageId: number,
    tenantId: number
  ): Promise<void> {
    try {
      const response = await axios.patch(
        `${API_BASE_URL}/lead/${leadId}/`,
        {
          lifecycle_type_stage_id: stageId,
          tenant_id: tenantId,
        },
        {
          headers: getAuthHeaders(),
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error updating lead stage:", error);
      throw error;
    }
  },
};
